<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.gamification_items', 2),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.edit'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    <div>
                        {{ $t('messages.information') }}
                    </div>
                    <div>
                        <img :src="gamificationItem.image_path" alt="" class="h-40" />
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-8">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.name')" for="name" />
                            <FormInput
                                required
                                id="name"
                                v-model="form.name"
                                name="name"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.image')" for="image" />
                            <input
                                id="image"
                                @input="form.image = $event.target.files[0]"
                                name="image"
                                type="file"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.image" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.description')" for="description" />
                            <FormTextarea
                                id="description"
                                v-model="form.description"
                                name="description"
                                type="text"
                                max="480"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.description" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_purchasable')" for="is_purchasable" />
                            <input
                                id="is_purchasable"
                                type="checkbox"
                                v-model="form.is_purchasable"
                                name="is_purchasable"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_purchasable" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1" :class="form.is_purchasable || form.is_sellable ? 'visible' : 'invisible'">
                            <FormLabel :value="$t('messages.price')" for="price" />
                            <FormInput
                                :required="form.is_purchasable"
                                id="price"
                                v-model.number="form.price"
                                name="price"
                                type="number"
                                step="1"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.price" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1" :class="form.is_purchasable ? 'visible' : 'invisible'">
                            <FormLabel :value="$t('messages.purchasable_at')" for="purchasable_at" />
                            <FormInput
                                :required="form.is_purchasable"
                                id="purchasable_at"
                                v-model="form.purchasable_at"
                                name="purchasable_at"
                                type="date"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.purchasable_at" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1" :class="form.is_purchasable ? 'visible' : 'invisible'">
                            <FormLabel :value="$t('messages.level_required')" for="price" />
                            <FormInput
                                id="level_required"
                                v-model.number="form.level_required"
                                name="level_required"
                                type="number"
                                step="1"
                                min="1"
                                :placeholder="$t('messages.(optional)')"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.level_required" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_sellable')" for="is_sellable" />
                            <input
                                id="is_sellable"
                                type="checkbox"
                                v-model="form.is_sellable"
                                name="is_sellable"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_sellable" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.is_tradable')" for="is_tradable" />
                            <input
                                id="is_tradable"
                                type="checkbox"
                                v-model="form.is_tradable"
                                name="is_tradable"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_tradable" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-4">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.draft')" for="is_draft" />
                            <input
                                id="is_draft"
                                type="checkbox"
                                v-model="form.is_draft"
                                name="is_draft"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.is_draft" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection :class="gamificationItem.type ? 'visible' : 'invisible'">
                <template v-slot:title>
                    {{ $t('messages.options') }}
                </template>

                <template v-if="gamificationItem.type">
                    <ItemSpecificOptionsForm
                        :type="gamificationItem.type"
                        v-model="form.options"
                        :errors="form.errors"
                    />
                </template>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection :class="gamificationItem.type ? 'visible' : 'invisible'">
                <template v-slot:title>
                    {{ $tc('messages.gamification_character_types', 2) }}
                </template>

                <div>
                    {{ $t('messages.limit_to_selected_characters') }}
                </div>
                <div class="flex flex-wrap gap-x-4 gap-y-1">
                    <label v-for="characterType in options.characterTypes" :key="characterType.value">
                        <input
                            v-model="form.gamification_character_type_ids"
                            name="gamification_character_type_id"
                            :value="characterType.value"
                            type="checkbox"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        {{ characterType.text }}
                    </label>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.usage_limitations') }}
                </template>

                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.has_stock_limited_stock')" for="uses_stock" />
                            <input
                                id="uses_stock"
                                type="checkbox"
                                v-model="form.uses_stock"
                                name="uses_stock"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <FormError :value="form.errors.uses_stock" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1" :class="form.uses_stock ? 'visible' : 'invisible'">
                            <FormLabel :value="$t('messages.stock_amount')" for="stock_amount" />
                            <FormInput
                                :required="form.uses_stock"
                                id="stock_amount"
                                v-model.number="form.stock_amount"
                                name="stock_amount"
                                type="number"
                                step="1"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.stock_amount" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.max_total_uses_per_person')" for="max_total_uses" />
                            <FormInput
                                id="max_total_uses"
                                v-model.number="form.max_total_uses"
                                name="max_total_uses"
                                type="number"
                                :placeholder="$t('messages.(optional)')"
                                step="1"
                                max="1000"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.max_total_uses" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <FormLabel :value="$t('messages.groups')" for="group_ids">
                            {{ $t('messages.(optional)') }}
                        </FormLabel>
                        <div class="mt-1 rounded-md shadow-sm">
                            <label class="ml-1 mr-3" v-for="group in options.groups" :key="group.value">
                                <input
                                    name="group_ids[]"
                                    type="checkbox"
                                    :value="group.value"
                                    v-model="form.group_ids"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <span class="inline-block text-sm leading-5 font-medium text-gray-700">
                                    {{ group.text }}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                    {{ $t('messages.save') }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import AppButton from '@/components/AppButton.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import ItemSpecificOptionsForm from '@/components/Gamification/Items/ItemSpecificOptionsForm.vue';

export default {
    components: {
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        AppButton,
        FormTextarea,
        ItemSpecificOptionsForm,
    },
    layout: Layout,
    name: 'Edit',
    props: ['teacher', 'meta', 'options', 'gamificationItem', 'gamificationSet'],
    data() {
        return {
            form: this.$inertia.form({
                _method: 'patch',
                name: this.gamificationItem.name,
                description: this.gamificationItem.description,
                price: this.gamificationItem.price,
                is_sellable: this.gamificationItem.is_sellable,
                is_tradable: this.gamificationItem.is_tradable,
                is_purchasable: this.gamificationItem.is_purchasable,
                purchasable_at: this.gamificationItem.purchasable_at,
                is_draft: this.gamificationItem.is_draft,
                image: null,
                level_required: this.gamificationItem.level_required,
                options: this.gamificationItem.options,
                gamification_character_type_ids: this.gamificationItem.gamification_character_type_ids,
                max_total_uses: this.gamificationItem.max_total_uses,
                uses_stock: this.gamificationItem.uses_stock,
                stock_amount: this.gamificationItem.stock_amount,
                group_ids: this.gamificationItem.group_ids,
            }),
        };
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.update, {
                forceFormData: true,
            });
        },
    },
};
</script>
